import { AxiosError } from 'axios';

import { AI_HTTP_CLIENT } from '@/api/ai/client';

import type {
  WebsitePreviewApiResponse,
  WebsitePreviewErrorCode,
} from './types';

/**
 * Website AI API utilities
 *
 * Quiz generation goes through useBuildAiQuizByWebsite (see api/ai/useBuildAiQuiz.ts).
 * This module owns only the preview metadata fetch.
 *
 * IMPORTANT: Uses AI_HTTP_CLIENT for automatic:
 * - Authorization header (Bearer token)
 * - X-Quiz-Server-Name header
 * - snake_case ↔ camelCase conversion
 * - baseURL configuration (NEXT_PUBLIC_AI_SERVER_BASEURL)
 * - LONG_REQUEST_TIMEOUT (5 minutes)
 */
export const websiteApi = {
  getPreview: async (url: string): Promise<WebsitePreviewApiResponse> => {
    const response = await AI_HTTP_CLIENT.get<WebsitePreviewApiResponse>(
      `/quiz-builder/website/preview`,
      {
        params: { url },
      },
    );
    return response.data;
  },
};

/**
 * Backend가 보낸 stable error code를 추출한다.
 * 응답 본문은 `{detail: {error_code, message}}` (FastAPI HTTPException) 모양.
 *
 * AI_HTTP_CLIENT의 snake→camel 변환이 nested detail 안쪽까지 적용되는지에
 * 환경 의존성이 있어, snake/camel 양쪽 키를 모두 확인한다.
 *
 * @returns 알려진 코드면 그 값, 아니면 undefined
 */
export const parseWebsiteErrorCode = (
  error: unknown,
): WebsitePreviewErrorCode | undefined => {
  if (!(error instanceof AxiosError)) {
    return undefined;
  }
  const detail = (error.response?.data as { detail?: unknown } | undefined)
    ?.detail;
  if (typeof detail !== 'object' || detail === null) {
    return undefined;
  }
  const candidate =
    (detail as Record<string, unknown>).errorCode ??
    (detail as Record<string, unknown>).error_code;
  if (typeof candidate !== 'string') {
    return undefined;
  }
  const knownCodes: ReadonlySet<WebsitePreviewErrorCode> =
    new Set<WebsitePreviewErrorCode>([
      'invalid_url',
      'invalid_scheme',
      'blocked_by_ssrf',
      'http_error',
      'timeout',
      'csr_detected',
      'empty_extraction',
      'oversize',
      'content_too_short',
      'login_required',
      'target_not_found',
    ]);
  return knownCodes.has(candidate as WebsitePreviewErrorCode)
    ? (candidate as WebsitePreviewErrorCode)
    : undefined;
};
